// Footer.js — Rodapé em CARTÃO "colado" na página, no lugar da antiga faixa de
// ponta a ponta. O desenho veio de uma referência do dono (cartão de cantos bem
// arredondados, fitas adesivas nos cantos, colunas de links esmaecidas); o que
// entrou foi a FORMA — o código da referência era Next.js + shadcn + lucide, e
// nada disso existe aqui.
//
// As três decisões que este arquivo concentra:
//
//  1. NAVY NOS DOIS TEMAS. O <footer> mantém a classe de escopo `.brand-dark`
//     (definida no App.css): a logo da editora é branca e sumiria num rodapé
//     claro. Por isso a hierarquia de claridade da referência foi INVERTIDA em
//     vez de copiada — o `bg-white` do cartão virou `insigne-navy-soft` (o
//     token de superfície de cartão) sobre `insigne-navy-deep` (o "papel" em
//     que ele está colado). O que a referência diz com claro-sobre-cinza, aqui
//     é dito com navy-claro-sobre-navy-escuro: a leitura "cartão apoiado num
//     fundo" é a mesma, e a logo continua legível nos dois temas.
//
//  2. SEM PÍLULA "soon". Na referência ela marca seções que ainda não existem.
//     Aqui todo link do rodapé aponta para rota registrada no App.js ou para
//     canal real (e-mail/WhatsApp) — não há o que marcar. Inventar "Termos" ou
//     "Privacidade" só para ter onde usar a pílula seria prometer página que
//     ninguém pediu.
//
//  3. MESMO CONTEÚDO DE ANTES. As quatro colunas viraram duas porque o rodapé
//     só tem dois assuntos (navegar / falar com a editora); no redesenho
//     nenhum link foi acrescentado nem removido. As redes continuam as quatro
//     que a editora de fato mantém, e o crédito do desenvolvedor desceu para a
//     linha de copyright, que é onde a referência põe o texto de rodapé legal.
//     Uma exceção entrou depois: "Todos os livros" na coluna Menu — a razão
//     está no comentário da montagem das colunas.
import React from "react";
import { Link } from "react-router-dom";
import logo from "../../assets/imgs/logo.png";
import FitaAdesiva from "../ui/FitaAdesiva";
import {
  WHATSAPP_HREF,
  WHATSAPP_PUBLICAR_HREF,
  EMAIL_PUBLICAR_HREF,
} from "../../config/contato";
import { OUTROS_SITES } from "../../config/sites";
import { Ornamento } from "../ui/TituloDeSecao";

const scrollToTop = () => window.scrollTo(0, 0);


const Footer = () => {
  const main = process.env.REACT_APP_MAIN;

  // O e-mail pré-preenchido e o WhatsApp saíram daqui para config/contato.js:
  // o número estava escrito à mão no rodapé E no cabeçalho, e foi assim que um
  // dos dois pôde ficar errado sem nada no código acusar.
  // Uma diferença de forma, não de destino: aqui o link era
  // `api.whatsapp.com/send?phone=`, e a constante usa `wa.me`. As duas abrem a
  // mesma conversa; ficou a curta, que é a que se lê sem interpretar query
  // string.

  // As quatro redes que a editora realmente mantém. Antes o LinkedIn vinha com
  // fundo laranja e os outros três só com o ícone solto — três pesos diferentes
  // numa fileira de quatro. Agora todos são o mesmo botão e o laranja é o hover.
  const socials = [
    { nome: "LinkedIn", icon: "bi-linkedin", href: "https://www.linkedin.com/company/insigneacad%C3%AAmica/" },
    { nome: "X", icon: "bi-twitter-x", href: "https://x.com/insigneacademica" },
    { nome: "Instagram", icon: "bi-instagram", href: "https://www.instagram.com/insigneacademica" },
    { nome: "Facebook", icon: "bi-facebook", href: "https://www.facebook.com/insigneacademica" },
  ];

  // `to` = rota interna (react-router); `href` = destino externo. Rótulos e
  // destinos são os mesmos do rodapé anterior — só o agrupamento mudou.
  const colunas = [
    {
      titulo: "Menu",
      itens: [
        { rotulo: "Início", to: main },
        // "Best-sellers" era o único caminho do rodapé para o acervo, e ele
        // chega ao catálogo já ordenado por popularidade — quem procurava
        // simplesmente "todos os livros" não tinha onde clicar. Vem antes do
        // best-seller porque é o destino mais amplo dos dois.
        { rotulo: "Todos os livros", to: main + "livros" },
        { rotulo: "Best-sellers", to: main + "livros?ordem=populares" },
        { rotulo: "Contato", to: main + "contato" },
      ],
    },
    {
      titulo: "Fale conosco",
      itens: [
        { rotulo: "E-mail", href: EMAIL_PUBLICAR_HREF },
        { rotulo: "Whatsapp", href: WHATSAPP_HREF },
        { rotulo: "Publique", href: WHATSAPP_PUBLICAR_HREF },
      ],
    },
    // Os outros dois sites da casa (18/10/2026), os mesmos da faixa laranja
    // do cabeçalho. Links absolutos: cada site é outro aplicativo.
    {
      titulo: "Nossos sites",
      itens: OUTROS_SITES.map((site) => ({ rotulo: site.rotulo, href: site.url, mesmaAba: true })),
    },
  ];

  // Link esmaecido das colunas. O `!` não é preciosismo: o reset do App.css
  // pinta todo <a> de `text-white` na camada base e o bootstrap.min.css entra
  // depois no bundle — sem ele o esmaecido some em parte dos casos.
  const linkCls = "text-sm !text-white/60 transition-colors hover:!text-insigne-orange";

  // ATENÇÃO ao escolher classe de espaçamento aqui: o bootstrap.min.css entra
  // DEPOIS do Tailwind no bundle e declara `.px-4`, `.mt-4`, `.mb-3`, `.gap-4`
  // (toda a escala 0–5) com !important e valores próprios — `px-4` viraria
  // 24 px, não 16. Por isso este arquivo só usa nomes que o Bootstrap não tem:
  // número >= 6 (px-6, mt-6, gap-6) ou fracionário (mb-3.5).
  return (
    <footer className="brand-dark border-t border-insigne-orange bg-insigne-navy-deep bg-insigne-texture bg-[length:3%] px-6 pb-8 pt-12 md:px-8">
      {/* ===== O cartão ===== */}
      <div className="relative mx-auto max-w-5xl bloco px-6 py-10 shadow-insigne-card md:px-10">
        {/* Ângulos diferentes de propósito: duas fitas no mesmo ângulo
            leem como carimbo, não como algo colado à mão. */}
        <FitaAdesiva className="hidden text-insigne-orange/80 md:block -left-8 -top-4" rotacao={-8} largura={78} />
        <FitaAdesiva className="hidden text-insigne-orange/80 md:block -right-8 -top-4" rotacao={97} largura={78} />

        <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
          {/* Marca + posicionamento. A frase não é nova: é a mesma que o painel
              de login já usa (components/login/AuthLayout.js). */}
          <div className="md:max-w-[17rem]">
            <Link to={main} onClick={scrollToTop} className="block">
              <img src={logo} alt="Insigne Acadêmica" className="h-12 w-auto" />
            </Link>
            {/* A frase na League Gothic, com o losango: o fecho de página dos
                outros dois sites. */}
            <p className="titulo-display mb-0 mt-6 text-[2rem] leading-[1] text-white">
              Conhecimento acadêmico ao alcance de <span className="text-insigne-orange">um clique</span>.
            </p>
            <Ornamento className="mt-[1rem]" />
          </div>

          {/* Colunas de links */}
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 sm:gap-12">
            {colunas.map((col) => (
              <div key={col.titulo}>
                <p className="chapeu mb-3.5 !text-[0.66rem]">{col.titulo}</p>
                <ul className="space-y-2">
                  {col.itens.map((item) => (
                    <li key={item.rotulo}>
                      {item.to ? (
                        <Link to={item.to} onClick={scrollToTop} className={linkCls}>
                          {item.rotulo}
                        </Link>
                      ) : (
                        <a
                          href={item.href}
                          target={item.mesmaAba ? undefined : "_blank"}
                          rel={item.mesmaAba ? undefined : "noreferrer"}
                          className={linkCls}
                        >
                          {item.rotulo}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== Linha de copyright — FORA do cartão =====
          O ano sai do relógio: o rodapé anterior tinha "2025" escrito à mão e
          envelhecia sozinho na virada do ano. */}
      <div className="mx-auto mt-8 flex max-w-5xl flex-col items-center gap-6 px-2 text-xs text-white/50 sm:flex-row sm:justify-between">
        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center sm:justify-start sm:text-left">
          <span>&copy; {new Date().getFullYear()}</span>
          {/* A marquinha `insigne.png` que ficava aqui saiu: ela é um wordmark
              deitado e, no corpo de 12 px desta linha, virava um borrão de
              20 px de largura. A logo já está inteira e legível no cartão logo
              acima — repetir ilegível não acrescentava marca nenhuma. */}
          <Link to={main} onClick={scrollToTop} className="font-semibold !text-insigne-orange">
            Insigne
          </Link>
          <span>· Todos os direitos reservados.</span>
          <span aria-hidden="true" className="hidden sm:inline">
            &middot;
          </span>
          <span>
            Projetado e desenvolvido por{" "}
            <span className="font-semibold text-white/70">Joás Leon</span>
          </span>
        </div>

        <div className="flex items-center gap-2">
          {socials.map((s) => (
            <a
              key={s.icon}
              href={s.href}
              target="_blank"
              rel="noreferrer"
              aria-label={s.nome}
              title={s.nome}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-line
                         bg-insigne-navy-soft text-base !text-white/70 transition-colors
                         hover:border-insigne-orange hover:!text-insigne-orange"
            >
              <i className={`bi ${s.icon}`} />
            </a>
          ))}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
